import fp from 'lodash/fp'
import { combine, withContext, withCallbacks } from 'react-hooks-combine'
import { withFormModel, withMobxAutorun } from 'shared/hooks'
import { spec, validators } from 'utils/validation'
import { ServicesContext } from 'services/context'
import logger from 'utils/logger'

import { toOptions } from 'routes/public/events/overview/components/filters/helpers'
import i18next from 'i18next'
import TournamentsFiltersComponent from './component'
import { toSearchableCategory } from './helpers'

const { custom } = validators

// Storage key for persisting filter state
const MARKETING_FILTER_KEY = 'tournaments_marketing_promotion_filter'
const DEFAULT_MARKETING_FILTER = { id: '', name: 'All' }

// Load saved filter value from localStorage
const loadSavedMarketingFilter = () => {
  try {
    const saved = localStorage.getItem(MARKETING_FILTER_KEY)
    if (saved === 'true') return { id: 'true', name: 'Yes' }
    if (saved === 'false') return { id: 'false', name: 'No' }
  } catch (e) {
    logger.warn('Unable to load tournaments marketing filter from localStorage', e)
    return DEFAULT_MARKETING_FILTER
  }
  return DEFAULT_MARKETING_FILTER
}

const persistMarketingFilter = (value) => {
  try {
    if (value) {
      localStorage.setItem(MARKETING_FILTER_KEY, value)
    } else {
      localStorage.removeItem(MARKETING_FILTER_KEY)
    }
  } catch (e) {
    logger.warn('Unable to persist tournaments marketing filter', e)
  }
}

export const TournamentsFilters = combine(
  withContext(
    'adminTournamentService',
    ServicesContext,
    ({ adminTournamentService }) => adminTournamentService
  ),

  withFormModel(
    {
      search: {
        initialValue: '',
        spec: spec.flow(custom.valid),
      },
      sportTypes: spec.flow(custom.valid),
      marketingPromotionSelect: {
        initialValue: loadSavedMarketingFilter(),
        spec: spec.flow(custom.valid),
      },
    },
    { id: 'tournaments-filters-form' }
  ),

  withCallbacks(
    {
      loadSportTypes: () => async () => {
        const sportTypes = toOptions(
          i18next.t('enum:sportType', { returnObjects: true })
        )
        return sportTypes
      },
      onSearchRequest: {
        deps: ['debounce', 'onSearch'],
        func: (_state, { debounce, onSearch }) => {
          return fp.isNumber(debounce)
            ? fp.debounce(debounce, onSearch)
            : onSearch
        },
      },
    },
    []
  ),

  withMobxAutorun(
    ({ firstRun, state: { form, onSearchRequest } }) => {
      const search = form.getControl('search')
      const sportTypes = form.getControl('sportTypes')
      const marketingSelectControl = form.getControl('marketingPromotionSelect')
      const tournamentName = search.value

      let sportTypesQuery = null
      if (sportTypes.value) {
        sportTypesQuery = toSearchableCategory(sportTypes.value)
      }

      // Extract id value from the selected option
      let marketingSelectValue = ''
      try {
        if (marketingSelectControl?.value) {
          if (
            typeof marketingSelectControl.value === 'object' &&
            marketingSelectControl.value?.id
          ) {
            marketingSelectValue = marketingSelectControl.value.id
          } else {
            marketingSelectValue = String(marketingSelectControl.value)
          }
        }
      } catch (e) {
        console.error('Error processing marketing selection:', e)
      }

      if (firstRun) {
        return
      }

      const queryParams = {
        tournamentName,
        sportTypes: sportTypesQuery,
      }

      // Persist the filter value to localStorage
      persistMarketingFilter(marketingSelectValue)

      if (marketingSelectValue === 'true') {
        queryParams.marketingPromotion = true
      } else if (marketingSelectValue === 'false') {
        queryParams.marketingPromotion = false
      }

      onSearchRequest(queryParams)
    },
    ['onSearchRequest']
  )
)(TournamentsFiltersComponent)
